import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import CrownTooltip from './Tooltip';
import { 
  Download, 
  Filter, 
  TrendingUp, 
  Users, 
  Target, 
  Clock,
  Calendar,
  Award,
  Activity,
  BarChart3,
  Trash2,
  Plus,
  ExternalLink
} from 'lucide-react';
import { useToast } from './Toast';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement
);

const StatisticsSection = ({ sessions, members, locations, organization }) => {
  const { addToast } = useToast();
  const [dateFilter, setDateFilter] = useState('30'); // Last 30 days
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [disciplineFilter, setDisciplineFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [showComparison, setShowComparison] = useState(true);
  const [advancedStats, setAdvancedStats] = useState({
    rejected_registrations: 0,
    unique_active_users: 0,
    top_active_members: [],
    top_diary_users: [],
    birth_number_completion: { percentage: 0, completed: 0, total: 0 },
    total_sessions: 0,
    total_members: 0
  });
  
  // Top Skyteledere state
  const [topSkyteledere, setTopSkyteledere] = useState([]);
  const [skytelederPeriod, setSkytelederPeriod] = useState('365');
  const [loadingSkyteledere, setLoadingSkyteledere] = useState(true);

  // Dugnad statistics state
  const [showAllDugnadMembers, setShowAllDugnadMembers] = useState(false);
  const [dugnadStats, setDugnadStats] = useState({ top5: [], allQualified: [] });
  const [isGeneratingDugnadPdf, setIsGeneratingDugnadPdf] = useState(false);
  const [showDugnadDateFilter, setShowDugnadDateFilter] = useState(false);
  const [dugnadDateFilter, setDugnadDateFilter] = useState('year'); // 'year', '30', '90', '365', 'all', 'custom'
  const [dugnadCustomStartDate, setDugnadCustomStartDate] = useState('');
  const [dugnadCustomEndDate, setDugnadCustomEndDate] = useState('');

  // Calculate date range based on filter
  const getDugnadDateRange = () => {
    const now = new Date();
    let startDate, endDate;
    
    switch (dugnadDateFilter) {
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
        break;
      case '30':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case '90':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case '365':
        startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        endDate = now;
        break;
      case 'all':
        startDate = new Date(2000, 0, 1);
        endDate = now;
        break;
      case 'custom':
        startDate = dugnadCustomStartDate ? new Date(dugnadCustomStartDate) : new Date(now.getFullYear(), 0, 1);
        endDate = dugnadCustomEndDate ? new Date(dugnadCustomEndDate + 'T23:59:59') : now;
        break;
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
    }
    
    return { startDate, endDate };
  };

  // Calculate Dugnad statistics based on selected date filter
  useEffect(() => {
    const { startDate, endDate } = getDugnadDateRange();
    
    // Count dugnads per member for selected period
    const dugnadCounts = {};
    
    sessions.forEach(session => {
      const activity = session.activity || session.training_type || '';
      if (!activity.toLowerCase().includes('dugnad')) return;
      
      const sessionDate = new Date(session.start_time);
      if (sessionDate < startDate || sessionDate > endDate) return;
      
      const userId = session.user_id;
      const userName = session.user_name || 'Ukjent';
      
      if (!dugnadCounts[userId]) {
        dugnadCounts[userId] = { userId, userName, count: 0 };
      }
      dugnadCounts[userId].count += 1;
    });
    
    // Convert to array and sort
    const sortedDugnads = Object.values(dugnadCounts)
      .filter(d => d.count > 0)
      .sort((a, b) => b.count - a.count);
    
    // All members with at least 1 dugnad, and all with 3+ dugnads (qualified)
    const allWithDugnads = sortedDugnads; // All members with > 0 dugnads
    const allQualified = sortedDugnads.filter(d => d.count >= 3);
    
    // Also get members who don't have enough dugnads
    const approvedMembers = members.filter(m => m.approved);
    const membersWithNotEnough = approvedMembers.filter(m => {
      const count = dugnadCounts[m.id]?.count || 0;
      return count < 3;
    });
    
    setDugnadStats({ allWithDugnads, allQualified, total: sortedDugnads.length, dugnadCounts, membersWithNotEnough, approvedMembers });
  }, [sessions, members, dugnadDateFilter, dugnadCustomStartDate, dugnadCustomEndDate]);

  // Generate PDF for members who have NOT met the dugnad requirement (Statistics page)
  const generateDugnadMissingPDF = async () => {
    setIsGeneratingDugnadPdf(true);
    
    try {
      const jsPDF = (await import('jspdf')).default;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const currentYear = new Date().getFullYear();
      const dugnadThreshold = 3;
      
      // Profile colors
      const primaryColor = [234, 179, 8]; // Yellow
      const darkBg = [31, 41, 55];
      const textWhite = [255, 255, 255];
      const textGray = [156, 163, 175];
      
      // Header
      pdf.setFillColor(...darkBg);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 45, pageWidth, 3, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.setTextColor(...textWhite);
      pdf.text('Dugnadsoversikt', margin, 20);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(...primaryColor);
      pdf.text(`Medlemmer som mangler dugnadskrav - ${currentYear}`, margin, 30);
      
      pdf.setFontSize(10);
      pdf.setTextColor(...textGray);
      pdf.text(organization?.name || 'Organisasjon', margin, 38);
      
      // Info box
      let yPos = 58;
      pdf.setFillColor(249, 250, 251);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 20, 3, 3, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      pdf.text(`Aktivitetstype: Dugnad`, margin + 5, yPos + 8);
      pdf.text(`Krav for gullkrone: ${dugnadThreshold} dugnader`, margin + 5, yPos + 15);
      
      const membersWithNotEnough = dugnadStats.membersWithNotEnough || [];
      const approvedMembers = dugnadStats.approvedMembers || [];
      
      pdf.setTextColor(...primaryColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${membersWithNotEnough.length} av ${approvedMembers.length} medlemmer mangler kravet`, pageWidth - margin - 70, yPos + 12);
      
      yPos += 30;
      
      // Table header
      pdf.setFillColor(...darkBg);
      pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...textWhite);
      pdf.text('Navn', margin + 5, yPos + 7);
      pdf.text('E-post', margin + 70, yPos + 7);
      pdf.text('Antall', pageWidth - margin - 25, yPos + 7);
      pdf.text('Mangler', pageWidth - margin - 10, yPos + 7);
      
      yPos += 12;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      // Sort by dugnad count (ascending)
      const sortedMembers = [...membersWithNotEnough].sort((a, b) => {
        const countA = dugnadStats.dugnadCounts?.[a.id]?.count || 0;
        const countB = dugnadStats.dugnadCounts?.[b.id]?.count || 0;
        return countA - countB;
      });
      
      sortedMembers.forEach((member, index) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = 20;
          pdf.setFillColor(...darkBg);
          pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(...textWhite);
          pdf.text('Navn', margin + 5, yPos + 7);
          pdf.text('E-post', margin + 70, yPos + 7);
          pdf.text('Antall', pageWidth - margin - 25, yPos + 7);
          pdf.text('Mangler', pageWidth - margin - 10, yPos + 7);
          yPos += 12;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
        }
        
        if (index % 2 === 0) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yPos - 3, pageWidth - (margin * 2), 8, 'F');
        }
        
        const count = dugnadStats.dugnadCounts?.[member.id]?.count || 0;
        const missing = dugnadThreshold - count;
        
        pdf.setTextColor(31, 41, 55);
        pdf.text(member.full_name?.substring(0, 35) || 'Ukjent', margin + 5, yPos + 2);
        
        pdf.setTextColor(107, 114, 128);
        pdf.text(member.email?.substring(0, 35) || '-', margin + 70, yPos + 2);
        
        if (count > 0) {
          pdf.setTextColor(234, 179, 8);
        } else {
          pdf.setTextColor(156, 163, 175);
        }
        pdf.text(count.toString(), pageWidth - margin - 23, yPos + 2);
        
        pdf.setTextColor(239, 68, 68);
        pdf.text(missing.toString(), pageWidth - margin - 5, yPos + 2);
        
        yPos += 8;
      });
      
      // Footer
      yPos = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setTextColor(...textGray);
      pdf.text(`Generert: ${new Date().toLocaleDateString('nb-NO')} kl. ${new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`, margin, yPos);
      pdf.text('Aktivlogg', pageWidth - margin - 15, yPos);
      
      const fileName = `dugnad-mangler-${currentYear}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      addToast(`PDF lastet ned: ${membersWithNotEnough.length} medlemmer mangler kravet`, 'success');
    } catch (error) {
      console.error('Failed to generate dugnad PDF:', error);
      addToast('Kunne ikke generere PDF', 'error');
    } finally {
      setIsGeneratingDugnadPdf(false);
    }
  };

  // Generate PDF for members who HAVE met the dugnad requirement (Statistics page)
  const generateDugnadCompletedPDF = async () => {
    setIsGeneratingDugnadPdf(true);
    
    try {
      const jsPDF = (await import('jspdf')).default;
      
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const currentYear = new Date().getFullYear();
      const dugnadThreshold = 3;
      
      // Profile colors
      const primaryColor = [34, 197, 94]; // Green
      const goldColor = [234, 179, 8]; // Yellow
      const darkBg = [31, 41, 55];
      const textWhite = [255, 255, 255];
      const textGray = [156, 163, 175];
      
      // Header
      pdf.setFillColor(...darkBg);
      pdf.rect(0, 0, pageWidth, 45, 'F');
      pdf.setFillColor(...primaryColor);
      pdf.rect(0, 45, pageWidth, 3, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.setTextColor(...textWhite);
      pdf.text('Dugnadsoversikt', margin, 20);
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.setTextColor(...goldColor);
      pdf.text(`Medlemmer med gullkrone-status - ${currentYear}`, margin, 30);
      
      pdf.setFontSize(10);
      pdf.setTextColor(...textGray);
      pdf.text(organization?.name || 'Organisasjon', margin, 38);
      
      // Info box
      let yPos = 58;
      pdf.setFillColor(240, 253, 244);
      pdf.roundedRect(margin, yPos, pageWidth - (margin * 2), 20, 3, 3, 'F');
      
      pdf.setFontSize(10);
      pdf.setTextColor(22, 101, 52);
      pdf.text(`Aktivitetstype: Dugnad`, margin + 5, yPos + 8);
      pdf.text(`Krav for gullkrone: ${dugnadThreshold} dugnader`, margin + 5, yPos + 15);
      
      const approvedMembers = dugnadStats.approvedMembers || [];
      const membersWithEnough = dugnadStats.allQualified || [];
      
      pdf.setTextColor(...goldColor);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${membersWithEnough.length} av ${approvedMembers.length} medlemmer har oppfylt kravet`, pageWidth - margin - 75, yPos + 12);
      
      yPos += 30;
      
      // Table header
      pdf.setFillColor(...darkBg);
      pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...textWhite);
      pdf.text('Navn', margin + 5, yPos + 7);
      pdf.text('E-post', margin + 70, yPos + 7);
      pdf.text('Antall', pageWidth - margin - 15, yPos + 7);
      
      yPos += 12;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      
      // Sort by dugnad count (descending)
      const sortedMembers = [...membersWithEnough].sort((a, b) => b.count - a.count);
      
      sortedMembers.forEach((member, index) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = 20;
          pdf.setFillColor(...darkBg);
          pdf.rect(margin, yPos, pageWidth - (margin * 2), 10, 'F');
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(10);
          pdf.setTextColor(...textWhite);
          pdf.text('Navn', margin + 5, yPos + 7);
          pdf.text('E-post', margin + 70, yPos + 7);
          pdf.text('Antall', pageWidth - margin - 15, yPos + 7);
          yPos += 12;
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
        }
        
        if (index % 2 === 0) {
          pdf.setFillColor(240, 253, 244);
          pdf.rect(margin, yPos - 3, pageWidth - (margin * 2), 8, 'F');
        }
        
        // Find member email from members array
        const memberData = members.find(m => m.id === member.userId);
        
        pdf.setTextColor(31, 41, 55);
        pdf.text(member.userName?.substring(0, 35) || 'Ukjent', margin + 5, yPos + 2);
        
        pdf.setTextColor(107, 114, 128);
        pdf.text(memberData?.email?.substring(0, 35) || '-', margin + 70, yPos + 2);
        
        pdf.setTextColor(234, 179, 8);
        pdf.text(member.count.toString(), pageWidth - margin - 12, yPos + 2);
        
        yPos += 8;
      });
      
      // Footer
      yPos = pageHeight - 15;
      pdf.setFontSize(8);
      pdf.setTextColor(...textGray);
      pdf.text(`Generert: ${new Date().toLocaleDateString('nb-NO')} kl. ${new Date().toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`, margin, yPos);
      pdf.text('Aktivlogg', pageWidth - margin - 15, yPos);
      
      const fileName = `dugnad-oppfylt-${currentYear}-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      addToast(`PDF lastet ned: ${membersWithEnough.length} medlemmer har oppfylt kravet`, 'success');
    } catch (error) {
      console.error('Failed to generate dugnad PDF:', error);
      addToast('Kunne ikke generere PDF', 'error');
    } finally {
      setIsGeneratingDugnadPdf(false);
    }
  };

  // Load advanced statistics
  useEffect(() => {
    const fetchAdvancedStats = async () => {
      try {
        const API_BASE = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_BASE}/api/admin/advanced-statistics`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAdvancedStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch advanced statistics:', error);
      }
    };

    fetchAdvancedStats();
  }, []);

  // Load Top Skyteledere
  useEffect(() => {
    const fetchTopSkyteledere = async () => {
      try {
        setLoadingSkyteledere(true);
        const API_BASE = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_BASE}/api/training/top-skyteledere?days=${skytelederPeriod}&limit=10`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setTopSkyteledere(data.skyteledere || []);
        }
      } catch (error) {
        console.error('Failed to fetch top skyteledere:', error);
      } finally {
        setLoadingSkyteledere(false);
      }
    };

    fetchTopSkyteledere();
  }, [skytelederPeriod]);

  // Filter sessions based on date and discipline
  const filteredSessions = useMemo(() => {
    let cutoffDate;
    let endDate = new Date(); // Default end date is now
    
    if (dateFilter === 'all') {
      // Show all sessions from the beginning
      cutoffDate = new Date(2000, 0, 1);
    } else if (dateFilter === 'custom') {
      // Use custom date range
      cutoffDate = customDateFrom ? new Date(customDateFrom) : new Date(2000, 0, 1);
      endDate = customDateTo ? new Date(customDateTo + 'T23:59:59') : new Date();
    } else {
      // Standard filter (7, 30, 90, 365 days)
      cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - parseInt(dateFilter));
    }

    return sessions.filter(session => {
      const sessionDate = new Date(session.start_time);
      const matchesDate = sessionDate >= cutoffDate && sessionDate <= endDate;
      
      if (disciplineFilter === 'all') return matchesDate;
      
      // Find location discipline
      const location = locations.find(loc => loc.id === session.location_id);
      return matchesDate && location?.discipline === disciplineFilter;
    });
  }, [sessions, dateFilter, customDateFrom, customDateTo, disciplineFilter, locations]);

  // Statistics calculations
  const stats = useMemo(() => {
    // Determine period in days
    let currentPeriod;
    let cutoffDate;
    
    if (dateFilter === 'all') {
      // For "all", use the number of days since the first session or a large number
      const firstSession = sessions.length > 0 ? 
        sessions.reduce((min, s) => new Date(s.start_time) < new Date(min.start_time) ? s : min, sessions[0]) : null;
      cutoffDate = firstSession ? new Date(firstSession.start_time) : new Date(2000, 0, 1);
      currentPeriod = Math.ceil((new Date() - cutoffDate) / (1000 * 60 * 60 * 24)) || 365;
    } else if (dateFilter === 'custom') {
      cutoffDate = customDateFrom ? new Date(customDateFrom) : new Date(2000, 0, 1);
      const endDate = customDateTo ? new Date(customDateTo) : new Date();
      currentPeriod = Math.ceil((endDate - cutoffDate) / (1000 * 60 * 60 * 24)) || 30;
    } else {
      currentPeriod = parseInt(dateFilter);
      cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - currentPeriod);
    }
    
    // Previous period for comparison
    const previousCutoffDate = new Date(cutoffDate);
    previousCutoffDate.setDate(previousCutoffDate.getDate() - currentPeriod);
    
    // Current period sessions
    const totalSessions = filteredSessions.length;
    const verifiedSessions = filteredSessions.filter(s => s.verified).length;
    const uniqueMembers = new Set(filteredSessions.map(s => s.user_id)).size;
    const avgSessionsPerDay = currentPeriod > 0 ? totalSessions / currentPeriod : 0;

    // Previous period sessions for comparison
    const previousPeriodSessions = sessions.filter(session => {
      const sessionDate = new Date(session.start_time);
      return sessionDate >= previousCutoffDate && sessionDate < cutoffDate;
    });
    
    const prevTotalSessions = previousPeriodSessions.length;
    const prevUniqueMembers = new Set(previousPeriodSessions.map(s => s.user_id)).size;
    const prevAvgSessionsPerDay = currentPeriod > 0 ? prevTotalSessions / currentPeriod : 0;

    // Calculate percentage changes
    const sessionsChange = prevTotalSessions > 0 ? ((totalSessions - prevTotalSessions) / prevTotalSessions * 100) : 0;
    const membersChange = prevUniqueMembers > 0 ? ((uniqueMembers - prevUniqueMembers) / prevUniqueMembers * 100) : 0;
    const avgChange = prevAvgSessionsPerDay > 0 ? ((avgSessionsPerDay - prevAvgSessionsPerDay) / prevAvgSessionsPerDay * 100) : 0;

    // Sessions by month for chart
    const sessionsByMonth = {};
    const months = [];
    
    // Generate last 6 months for better comparison
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const monthKey = date.toLocaleDateString('no-NO', { 
        year: 'numeric', 
        month: 'short' 
      });
      months.push(monthKey);
      sessionsByMonth[monthKey] = 0;
    }
    
    // Count sessions for each month
    sessions.forEach(session => {
      const sessionDate = new Date(session.start_time);
      const month = sessionDate.toLocaleDateString('no-NO', { 
        year: 'numeric', 
        month: 'short' 
      });
      if (sessionsByMonth.hasOwnProperty(month)) {
        sessionsByMonth[month]++;
      }
    });

    // Sessions by location
    const sessionsByLocation = {};
    filteredSessions.forEach(session => {
      const locationName = session.location_name || 'Ukjent';
      sessionsByLocation[locationName] = (sessionsByLocation[locationName] || 0) + 1;
    });

    // Sessions by discipline
    const sessionsByDiscipline = { nsf: 0, dfs: 0, dssn: 0 };
    filteredSessions.forEach(session => {
      const location = locations.find(loc => loc.id === session.location_id);
      const discipline = location?.discipline || 'nsf';
      sessionsByDiscipline[discipline]++;
    });

    // NEW: Sessions by activity type
    const sessionsByActivity = {};
    filteredSessions.forEach(session => {
      const activityName = session.activity || 'Ukjent aktivitet';
      sessionsByActivity[activityName] = (sessionsByActivity[activityName] || 0) + 1;
    });
    
    // Sort activities by count and get top 5
    const topActivities = Object.entries(sessionsByActivity)
      .sort(([, countA], [, countB]) => countB - countA)
      .slice(0, 5)
      .map(([name, count]) => ({ name, count }));

    // Active members (members with sessions in period)
    const activeMembers = members.filter(member => 
      filteredSessions.some(session => session.user_id === member.id)
    );

    return {
      totalSessions,
      verifiedSessions,
      uniqueMembers,
      avgSessionsPerDay,
      sessionsByMonth,
      sessionsByLocation,
      sessionsByDiscipline,
      topActivities, // NEW: Top activities
      activeMembers: activeMembers.length,
      verificationRate: totalSessions > 0 ? (verifiedSessions / totalSessions * 100) : 0,
      // Comparison data
      comparison: {
        sessions: { 
          current: totalSessions, 
          previous: prevTotalSessions, 
          change: sessionsChange 
        },
        members: { 
          current: uniqueMembers, 
          previous: prevUniqueMembers, 
          change: membersChange 
        },
        avgDaily: { 
          current: avgSessionsPerDay, 
          previous: prevAvgSessionsPerDay, 
          change: avgChange 
        }
      },
      months
    };
  }, [filteredSessions, members, locations, dateFilter, customDateFrom, customDateTo, sessions]);

  // Chart data
  const monthlyChartData = {
    labels: stats.months,
    datasets: [
      {
        label: 'Treningsøkter',
        data: stats.months.map(month => stats.sessionsByMonth[month] || 0),
        backgroundColor: 'rgba(255, 215, 0, 0.6)',
        borderColor: 'rgba(255, 215, 0, 1)',
        borderWidth: 2,
        tension: 0.1
      }
    ]
  };

  const locationChartData = {
    labels: Object.keys(stats.sessionsByLocation),
    datasets: [
      {
        label: 'Treningsøkter per bane',
        data: Object.values(stats.sessionsByLocation),
        backgroundColor: [
          'rgba(255, 215, 0, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(168, 85, 247, 0.8)'
        ]
      }
    ]
  };

  const disciplineChartData = {
    labels: ['NSF', 'DFS', 'DSSN'],
    datasets: [
      {
        label: 'Treningsøkter', // ADDED: Fix "undefined" issue
        data: [stats.sessionsByDiscipline.nsf, stats.sessionsByDiscipline.dfs, stats.sessionsByDiscipline.dssn],
        backgroundColor: ['#FFD700', '#10B981', '#3B82F6'],
        borderWidth: 2
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#fff' }
      }
    },
    scales: {
      x: { 
        ticks: { color: '#9CA3AF' },
        grid: { color: 'rgba(156, 163, 175, 0.1)' }
      },
      y: { 
        ticks: { color: '#9CA3AF' },
        grid: { color: 'rgba(156, 163, 175, 0.1)' }
      }
    }
  };

  // Helper function to capture chart as image
  const captureChartAsImage = async (chartId) => {
    try {
      const chartElement = document.getElementById(chartId);
      if (!chartElement) return null;
      
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(chartElement, {
        backgroundColor: '#1f2937', // Gray-800 background
        scale: 1.5, // Redusert fra 2 til 1.5 for mindre filstørrelse
        useCORS: true,
        allowTaint: true,
        logging: false, // Slår av logging for bedre performance
        removeContainer: true
      });
      
      // Konverter til JPEG med 75% kvalitet for bedre kompresjon
      return canvas.toDataURL('image/jpeg', 0.75);
    } catch (error) {
      console.error('Failed to capture chart:', error);
      return null;
    }
  };

  // Enhanced PDF export with charts and professional design
  const exportStatisticsPDF = async () => {
    setIsExporting(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      
      // Capture charts as images first
      addToast('Genererer grafer...', 'info');
      const monthlyChart = await captureChartAsImage('monthly-chart');
      const locationChart = await captureChartAsImage('location-chart');
      const disciplineChart = await captureChartAsImage('discipline-chart');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm', 
        format: 'a4',
        compress: true, // Aktiverer PDF-kompresjon
        precision: 2    // Reduserer presisjon for mindre filer
      });
      let yPos = 20;

      // Professional header with gradient-like effect
      pdf.setFillColor(255, 215, 0); // Gold
      pdf.rect(0, 0, 210, 35, 'F');
      pdf.setFillColor(255, 235, 59); // Lighter gold
      pdf.rect(0, 25, 210, 10, 'F');
      
      // Main title
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AKTIVITETSSTATISTIKK', 105, 18, { align: 'center' });
      
      // Subtitle
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Detaljert analyse av klubbens treningsaktivitet', 105, 26, { align: 'center' });
      
      yPos = 50;
      
      // Organization info section
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${organization?.name || 'Organisasjon'}`, 20, yPos);
      yPos += 8;
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Rapport generert: ${new Date().toLocaleDateString('no-NO', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })}`, 20, yPos);
      yPos += 6;
      
      // Period text based on filter type
      let periodText;
      if (dateFilter === 'all') {
        periodText = 'Alle treningsøkter fra start';
      } else if (dateFilter === 'custom') {
        const fromDate = customDateFrom ? new Date(customDateFrom).toLocaleDateString('no-NO') : 'Start';
        const toDate = customDateTo ? new Date(customDateTo).toLocaleDateString('no-NO') : 'Nå';
        periodText = `Egendefinert periode: ${fromDate} - ${toDate}`;
      } else {
        periodText = `Siste ${dateFilter} dager`;
      }
      pdf.text(`Tidsperiode: ${periodText}`, 20, yPos);
      yPos += 15;

      // Key Metrics Cards Section
      pdf.setFillColor(240, 240, 240);
      pdf.rect(15, yPos - 5, 180, 55, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('NØKKELSTATISTIKK', 20, yPos + 5);
      yPos += 15;

      // Create metric cards layout (2x2 grid)
      const cardWidth = 85;
      const cardHeight = 15;
      const cardSpacing = 5;
      
      // Top row
      pdf.setFillColor(59, 130, 246); // Blue
      pdf.rect(20, yPos, cardWidth, cardHeight, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Totale økter', 22, yPos + 5);
      pdf.setFontSize(16);
      pdf.text(stats.totalSessions.toString(), 22, yPos + 12);
      
      pdf.setFillColor(34, 197, 94); // Green
      pdf.rect(110, yPos, cardWidth, cardHeight, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Aktive medlemmer', 112, yPos + 5);
      pdf.setFontSize(16);
      pdf.text(stats.uniqueMembers.toString(), 112, yPos + 12);
      
      yPos += cardHeight + cardSpacing;
      
      // Bottom row
      pdf.setFillColor(255, 193, 7); // Yellow
      pdf.rect(20, yPos, cardWidth, cardHeight, 'F');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Verifisering', 22, yPos + 5);
      pdf.setFontSize(16);
      pdf.text(`${stats.verificationRate.toFixed(1)}%`, 22, yPos + 12);
      
      pdf.setFillColor(168, 85, 247); // Purple
      pdf.rect(110, yPos, cardWidth, cardHeight, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Snitt per dag', 112, yPos + 5);
      pdf.setFontSize(16);
      pdf.text(stats.avgSessionsPerDay.toFixed(1), 112, yPos + 12);
      
      yPos += 25;

      // Advanced Statistics Section
      pdf.setFillColor(250, 250, 250);
      pdf.rect(15, yPos, 180, 45, 'F');
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AVANSERTE STATISTIKKER', 20, yPos + 8);
      yPos += 18;

      // Advanced stats in columns
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      
      // Left column
      pdf.text(`Avviste registreringer: ${advancedStats.rejected_registrations}`, 20, yPos);
      yPos += 6;
      pdf.text(`Unike aktive brukere: ${advancedStats.unique_active_users}`, 20, yPos);
      yPos += 6;
      pdf.text(`Fødselsnummer registrert: ${advancedStats.birth_number_completion.percentage}% (${advancedStats.birth_number_completion.completed}/${advancedStats.birth_number_completion.total})`, 20, yPos);
      
      yPos += 20;

      // Top 5 Lists Section
      if (advancedStats.top_active_members.length > 0 || advancedStats.top_diary_users.length > 0) {
        pdf.addPage();
        yPos = 20;
        
        // Page header
        pdf.setFillColor(255, 215, 0);
        pdf.rect(0, 0, 210, 25, 'F');
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('TOP LISTER', 105, 15, { align: 'center' });
        
        yPos = 40;
        
        // Top Active Members
        if (advancedStats.top_active_members.length > 0) {
          pdf.setFillColor(240, 240, 240);
          pdf.rect(15, yPos - 5, 180, 8 + (advancedStats.top_active_members.length * 6), 'F');
          
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('TOP 5 MEST AKTIVE MEDLEMMER', 20, yPos);
          yPos += 10;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          advancedStats.top_active_members.forEach((member, index) => {
            const medals = ['1.', '2.', '3.', '4.', '5.'];
            pdf.text(`${medals[index]} ${member.name}: ${member.session_count} økter`, 25, yPos);
            yPos += 6;
          });
          yPos += 10;
        }
        
        // Top Diary Users
        if (advancedStats.top_diary_users.length > 0) {
          pdf.setFillColor(240, 255, 240);
          pdf.rect(15, yPos - 5, 180, 8 + (advancedStats.top_diary_users.length * 6), 'F');
          
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('TOP 5 SKYTEDAGBOK BRUKERE', 20, yPos);
          yPos += 10;
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          advancedStats.top_diary_users.forEach((user, index) => {
            const medals = ['1.', '2.', '3.', '4.', '5.'];
            pdf.text(`${medals[index]} ${user.name}: ${user.diary_entries} oppføringer`, 25, yPos);
            yPos += 6;
          });
        }
      }

      // Charts Section
      pdf.addPage();
      yPos = 20;
      
      // Charts page header
      pdf.setFillColor(255, 215, 0);
      pdf.rect(0, 0, 210, 25, 'F');
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AKTIVITETSGRAFER', 105, 15, { align: 'center' });
      
      yPos = 40;

      // Monthly trend chart
      if (monthlyChart) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Månedlig aktivitetstrend', 20, yPos);
        yPos += 10;
        pdf.addImage(monthlyChart, 'JPEG', 20, yPos, 170, 70, undefined, 'MEDIUM');
        yPos += 80;
      }

      // Location chart
      if (locationChart) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Aktivitet per bane', 20, yPos);
        yPos += 10;
        pdf.addImage(locationChart, 'JPEG', 20, yPos, 170, 70, undefined, 'MEDIUM');
        yPos += 80;
      }

      // Add another page if we have the discipline chart
      if (disciplineChart && yPos > 200) {
        pdf.addPage();
        yPos = 30;
      }

      // Discipline chart
      if (disciplineChart) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Fordeling per disiplin', 20, yPos);
        yPos += 10;
        pdf.addImage(disciplineChart, 'JPEG', 20, yPos, 170, 70, undefined, 'MEDIUM');
      }

      // Footer on all pages
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(128, 128, 128);
        pdf.text('Generert av Aktivlogg - www.aktivlogg.no', 105, 290, { align: 'center' });
        pdf.text(`Side ${i} av ${pageCount}`, 190, 290, { align: 'right' });
      }

      // Save PDF
      const today = new Date().toISOString().split('T')[0];
      const orgName = (organization?.name || 'Statistikk').replace(/[^a-zA-Z0-9æøå]/g, '-');
      pdf.save(`aktivitetsstatistikk-${orgName}-${today}.pdf`);
      
      addToast('Profesjonell statistikk-rapport med grafer eksportert!', 'success');
    } catch (error) {
      console.error('Export failed:', error);
      addToast('Kunne ikke eksportere statistikk. Prøv igjen.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Presentasjonsvisning (Digital Signage) – markedsføring øverst */}
      <SignagePromo />

      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="h-6 w-6 mr-2" />
              Aktivitetsstatistikk
            </h2>
            <p className="text-gray-800 mt-1">Detaljert analyse av klubbens treningsaktivitet</p>
          </div>
          <button
            onClick={exportStatisticsPDF}
            disabled={isExporting}
            className="mt-4 sm:mt-0 flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? 'Genererer PDF med grafer...' : 'Last ned PDF med grafer'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Tidsperiode
            </label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="7">Siste 7 dager</option>
              <option value="30">Siste 30 dager</option>
              <option value="90">Siste 90 dager</option>
              <option value="365">Siste år</option>
              <option value="all">Vis alt fra start</option>
              <option value="custom">Egendefinert periode</option>
            </select>
            
            {/* Custom Date Range Picker */}
            {dateFilter === 'custom' && (
              <div className="mt-3 p-3 bg-gray-700/50 rounded-lg border border-gray-600">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Fra dato</label>
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Til dato</label>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <Filter className="h-4 w-4 inline mr-1" />
              Disiplin
            </label>
            <select
              value={disciplineFilter}
              onChange={(e) => setDisciplineFilter(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="all">Alle disipliner</option>
              {/* Dynamically generate discipline options from organization shooting_branches */}
              {organization?.shooting_branches && Object.entries(organization.shooting_branches)
                .filter(([key, enabled]) => enabled)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([discipline, enabled]) => (
                  <option key={discipline} value={discipline}>{discipline.toUpperCase()}</option>
                ))}
            </select>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <TrendingUp className="h-4 w-4 inline mr-1" />
              Sammenligning
            </label>
            <button
              onClick={() => setShowComparison(!showComparison)}
              className={`w-full px-3 py-2 rounded-lg border transition-colors ${
                showComparison 
                  ? 'bg-yellow-600 border-yellow-500 text-white' 
                  : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {showComparison ? 'Skjul sammenligning' : 'Vis sammenligning'}
            </button>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Totale økter</p>
              <p className="text-3xl font-bold">{stats.totalSessions}</p>
              {showComparison && (
                <div className="flex items-center mt-1">
                  <span className={`text-xs flex items-center ${
                    stats.comparison.sessions.change > 0 ? 'text-green-200' : 
                    stats.comparison.sessions.change < 0 ? 'text-red-200' : 'text-blue-200'
                  }`}>
                    <TrendingUp className={`h-3 w-3 mr-1 ${
                      stats.comparison.sessions.change < 0 ? 'rotate-180' : ''
                    }`} />
                    {Math.abs(stats.comparison.sessions.change).toFixed(0)}% vs forrige periode
                  </span>
                </div>
              )}
            </div>
            <Activity className="h-8 w-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Aktive medlemmer</p>
              <p className="text-3xl font-bold">{stats.uniqueMembers}</p>
              {showComparison && (
                <div className="flex items-center mt-1">
                  <span className={`text-xs flex items-center ${
                    stats.comparison.members.change > 0 ? 'text-green-200' : 
                    stats.comparison.members.change < 0 ? 'text-red-200' : 'text-green-200'
                  }`}>
                    <TrendingUp className={`h-3 w-3 mr-1 ${
                      stats.comparison.members.change < 0 ? 'rotate-180' : ''
                    }`} />
                    {Math.abs(stats.comparison.members.change).toFixed(0)}% vs forrige periode
                  </span>
                </div>
              )}
            </div>
            <Users className="h-8 w-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-600 to-yellow-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Verifisering</p>
              <p className="text-3xl font-bold">{stats.verificationRate.toFixed(1)}%</p>
              <p className="text-yellow-200 text-xs mt-1">{stats.verifiedSessions} av {stats.totalSessions}</p>
            </div>
            <Award className="h-8 w-8 text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Snitt per dag</p>
              <p className="text-3xl font-bold">{stats.avgSessionsPerDay.toFixed(1)}</p>
              {showComparison && (
                <div className="flex items-center mt-1">
                  <span className={`text-xs flex items-center ${
                    stats.comparison.avgDaily.change > 0 ? 'text-green-200' : 
                    stats.comparison.avgDaily.change < 0 ? 'text-red-200' : 'text-purple-200'
                  }`}>
                    <TrendingUp className={`h-3 w-3 mr-1 ${
                      stats.comparison.avgDaily.change < 0 ? 'rotate-180' : ''
                    }`} />
                    {Math.abs(stats.comparison.avgDaily.change).toFixed(0)}% vs forrige periode
                  </span>
                </div>
              )}
            </div>
            <TrendingUp className="h-8 w-8 text-purple-200" />
          </div>
        </div>

        {/* Dugnad Crown Status - Only if feature enabled */}
        {(organization?.dugnad_feature_enabled !== false) && (
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-100 text-sm">Medl. med 3 dugnader i år</p>
              <p className="text-3xl font-bold">{dugnadStats.allQualified?.length || 0}</p>
              <p className="text-amber-200 text-xs mt-1">≥3 dugnader i {new Date().getFullYear()}</p>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" className="text-amber-200">
              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="currentColor" opacity="0.7"/>
              <path d="M19 19H5v-2h14v2z" fill="currentColor" opacity="0.7"/>
            </svg>
          </div>
        </div>
        )}
      </div>

      {/* Advanced Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Rejected Registrations */}
        <div className="bg-gradient-to-br from-red-600 to-red-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-100 text-sm">Avviste registreringer</p>
              <p className="text-3xl font-bold">{advancedStats.rejected_registrations}</p>
              <p className="text-red-200 text-xs mt-1">Totalt</p>
            </div>
            <Clock className="h-8 w-8 text-red-200" />
          </div>
        </div>

        {/* Total Logins (Active Users) */}
        <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm">Unike aktive brukere</p>
              <p className="text-3xl font-bold">{advancedStats.unique_active_users}</p>
              <p className="text-indigo-200 text-xs mt-1">Med treningsøkter</p>
            </div>
            <Users className="h-8 w-8 text-indigo-200" />
          </div>
        </div>

        {/* Birth Number Completion */}
        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-teal-100 text-sm">Fødselsnummer registrert</p>
              <p className="text-3xl font-bold">{advancedStats.birth_number_completion.percentage}%</p>
              <p className="text-teal-200 text-xs mt-1">
                {advancedStats.birth_number_completion.completed} av {advancedStats.birth_number_completion.total}
              </p>
            </div>
            <Award className="h-8 w-8 text-teal-200" />
          </div>
        </div>
      </div>

      {/* Top Users Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Active Members */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2 text-yellow-500" />
            Top 5 Mest Aktive Medlemmer
          </h3>
          <div className="space-y-3">
            {advancedStats.top_active_members.length > 0 ? (
              advancedStats.top_active_members.map((member, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-500 text-gray-900' :
                      index === 1 ? 'bg-gray-300 text-gray-900' :
                      index === 2 ? 'bg-yellow-600 text-white' :
                      'bg-gray-600 text-gray-300'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="text-white ml-3 font-medium">{member.name}</span>
                  </div>
                  <span className="text-yellow-400 font-bold">{member.session_count} økter</span>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">Ingen data tilgjengelig</p>
            )}
          </div>
        </div>

        {/* Top Diary Users */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-green-500" />
            Top 5 Skytedagbok Brukere
          </h3>
          <div className="space-y-3">
            {advancedStats.top_diary_users.length > 0 ? (
              advancedStats.top_diary_users.map((user, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-green-500 text-gray-900' :
                      index === 1 ? 'bg-gray-300 text-gray-900' :
                      index === 2 ? 'bg-green-600 text-white' :
                      'bg-gray-600 text-gray-300'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="text-white ml-3 font-medium">{user.name}</span>
                  </div>
                  <span className="text-green-400 font-bold">{user.diary_entries} oppføringer</span>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">Ingen data tilgjengelig</p>
            )}
          </div>
        </div>
      </div>

      {/* NEW: Most Used Activities */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Activity className="h-5 w-5 mr-2 text-blue-500" />
          Top 5 Mest Benyttede Aktiviteter
        </h3>
        <div className="space-y-3">
          {stats.topActivities.length > 0 ? (
            stats.topActivities.map((activity, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    index === 0 ? 'bg-blue-500 text-white' :
                    index === 1 ? 'bg-blue-400 text-white' :
                    index === 2 ? 'bg-blue-300 text-gray-900' :
                    'bg-gray-600 text-gray-300'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="text-white ml-3 font-medium">{activity.name}</span>
                </div>
                <span className="text-blue-400 font-bold">{activity.count} økter</span>
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-center py-4">Ingen aktivitetsdata tilgjengelig</p>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Activity */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            Månedlig aktivitetstrend
          </h3>
          <div id="monthly-chart" className="h-64">
            <Line data={monthlyChartData} options={chartOptions} />
          </div>
        </div>

        {/* Location Activity */}
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2" />
            Aktivitet per bane
          </h3>
          <div id="location-chart" className="h-64">
            <Pie data={locationChartData} options={{
              ...chartOptions,
              scales: undefined
            }} />
          </div>
        </div>
      </div>

      {/* Discipline Distribution */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Target className="h-5 w-5 mr-2" />
          Fordeling per disiplin
        </h3>
        <div id="discipline-chart" className="h-64">
          <Bar 
            data={disciplineChartData} 
            options={{
              ...chartOptions,
              indexAxis: 'y'
            }} 
          />
        </div>
      </div>

      {/* Top 10 Aktive Skyteledere/Admins */}
      <div className="bg-gradient-to-br from-gray-800 via-gray-800 to-yellow-900/20 rounded-lg p-6 border border-yellow-500/20">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <Award className="h-6 w-6 mr-2 text-yellow-400" />
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
              Top 10 Aktive Skyteledere
            </span>
          </h3>
          <select
            value={skytelederPeriod}
            onChange={(e) => setSkytelederPeriod(e.target.value)}
            className="bg-gray-700 text-white text-sm rounded-lg px-3 py-1.5 border border-gray-600 focus:ring-2 focus:ring-yellow-500"
          >
            <option value="30">Siste 30 dager</option>
            <option value="90">Siste 3 måneder</option>
            <option value="180">Siste 6 måneder</option>
            <option value="365">Siste 12 måneder</option>
          </select>
        </div>
        
        {loadingSkyteledere ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400"></div>
          </div>
        ) : topSkyteledere.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Users className="h-12 w-12 mb-3 opacity-50" />
            <p>Ingen verifiserte treningsøkter i denne perioden</p>
          </div>
        ) : (
          <div className="space-y-3">
            {topSkyteledere.map((skyteleder, index) => {
              const maxSessions = topSkyteledere[0]?.sessions_verified || 1;
              const percentage = (skyteleder.sessions_verified / maxSessions) * 100;
              
              // Medal colors for top 3
              const medalColors = {
                0: 'from-yellow-400 to-yellow-600', // Gold
                1: 'from-gray-300 to-gray-500',     // Silver
                2: 'from-orange-400 to-orange-600'  // Bronze
              };
              
              const isTopThree = index < 3;
              
              return (
                <div 
                  key={index}
                  className={`relative overflow-hidden rounded-lg transition-all duration-300 hover:scale-[1.02] ${
                    isTopThree ? 'bg-gray-700/80' : 'bg-gray-700/50'
                  }`}
                >
                  {/* Progress bar background */}
                  <div 
                    className={`absolute inset-0 opacity-20 ${
                      isTopThree 
                        ? `bg-gradient-to-r ${medalColors[index]}` 
                        : 'bg-gradient-to-r from-blue-500 to-blue-600'
                    }`}
                    style={{ width: `${percentage}%` }}
                  />
                  
                  <div className="relative flex items-center p-4">
                    {/* Rank badge */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg mr-4 ${
                      isTopThree 
                        ? `bg-gradient-to-br ${medalColors[index]} text-gray-900 shadow-lg` 
                        : 'bg-gray-600 text-gray-300'
                    }`}>
                      {index + 1}
                    </div>
                    
                    {/* Name and details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className={`font-semibold truncate ${isTopThree ? 'text-white' : 'text-gray-200'}`}>
                          {skyteleder.name}
                        </h4>
                        {index === 0 && (
                          <span className="text-yellow-400 text-lg">👑</span>
                        )}
                      </div>
                      {skyteleder.last_activity && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Sist aktiv: {skyteleder.last_activity}
                        </p>
                      )}
                    </div>
                    
                    {/* Session count with tooltip */}
                    <div className="flex-shrink-0 text-right">
                      <div 
                        className={`text-2xl font-bold cursor-help ${
                          isTopThree 
                            ? 'bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent' 
                            : 'text-blue-400'
                        }`}
                        title="Antall unike dager med skyteaktivitet"
                      >
                        {skyteleder.sessions_verified}
                      </div>
                      <div className="text-xs text-gray-400">treningsdager</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Summary footer */}
        {topSkyteledere.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-400">
                Totalt unike treningsdager ledet:
              </span>
              <span className="font-bold text-yellow-400">
                {topSkyteledere.reduce((sum, s) => sum + s.sessions_verified, 0)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Dugnad Statistics Section - Full Width (only if feature enabled) */}
      {(organization?.dugnad_feature_enabled !== false) && (
      <div className="col-span-full bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700 shadow-lg">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#EAB308"/>
                <path d="M19 19H5v-2h14v2z" fill="#EAB308"/>
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">
                Dugnadsdeltakelse {dugnadDateFilter === 'year' ? `i ${new Date().getFullYear()}` : 
                  dugnadDateFilter === '30' ? '(siste 30 dager)' :
                  dugnadDateFilter === '90' ? '(siste 90 dager)' :
                  dugnadDateFilter === '365' ? '(siste 365 dager)' :
                  dugnadDateFilter === 'all' ? '(alle)' :
                  dugnadDateFilter === 'custom' ? '(egendefinert)' : ''}
              </h3>
              <p className="text-sm text-gray-400">
                {dugnadStats.allQualified?.length || 0} medlemmer har oppnådd gullkrone-status (≥3 dugnader)
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3 text-sm">
            <span className="flex items-center gap-1 text-yellow-400">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="currentColor"/>
                <path d="M19 19H5v-2h14v2z" fill="currentColor"/>
              </svg>
              Gullkrone = ≥3 dugnader
            </span>
          </div>
        </div>
        
        {/* Date Filter Toggle */}
        <div className="mb-4">
          <button
            onClick={() => setShowDugnadDateFilter(!showDugnadDateFilter)}
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
          >
            <Filter className="h-4 w-4" />
            Filtrer tidsperiode
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
              className={`transition-transform ${showDugnadDateFilter ? 'rotate-180' : ''}`}
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          
          {showDugnadDateFilter && (
            <div className="mt-3 p-4 bg-gray-700/50 rounded-lg border border-gray-600">
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  { value: 'year', label: `I år (${new Date().getFullYear()})` },
                  { value: '30', label: 'Siste 30 dager' },
                  { value: '90', label: 'Siste 90 dager' },
                  { value: '365', label: 'Siste 365 dager' },
                  { value: 'all', label: 'Alle' },
                  { value: 'custom', label: 'Egendefinert' },
                ].map(option => (
                  <button
                    key={option.value}
                    onClick={() => setDugnadDateFilter(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      dugnadDateFilter === option.value
                        ? 'bg-yellow-500 text-gray-900 font-medium'
                        : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              
              {dugnadDateFilter === 'custom' && (
                <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-600">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Fra:</label>
                    <input
                      type="date"
                      value={dugnadCustomStartDate}
                      onChange={(e) => setDugnadCustomStartDate(e.target.value)}
                      className="px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-400">Til:</label>
                    <input
                      type="date"
                      value={dugnadCustomEndDate}
                      onChange={(e) => setDugnadCustomEndDate(e.target.value)}
                      className="px-3 py-1.5 bg-gray-600 border border-gray-500 rounded-lg text-white text-sm"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* PDF Download buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <button
            onClick={generateDugnadMissingPDF}
            disabled={isGeneratingDugnadPdf}
            className="flex items-center gap-2 px-3 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-600 disabled:cursor-wait text-gray-900 font-medium rounded-lg transition-colors text-sm"
            title="Last ned PDF-liste over medlemmer som mangler dugnadskravet"
          >
            {isGeneratingDugnadPdf ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Genererer...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Mangler krav (PDF)
              </>
            )}
          </button>
          
          <button
            onClick={generateDugnadCompletedPDF}
            disabled={isGeneratingDugnadPdf}
            className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-400 disabled:bg-green-600 disabled:cursor-wait text-white font-medium rounded-lg transition-colors text-sm"
            title="Last ned PDF-liste over medlemmer som har oppfylt dugnadskravet"
          >
            {isGeneratingDugnadPdf ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Genererer...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Oppfyller krav (PDF)
              </>
            )}
          </button>
        </div>

        {/* All Dugnad Members */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-300 mb-3">Alle med dugnadsdeltakelse ({dugnadStats.allWithDugnads?.length || 0})</h4>
          
          {(!dugnadStats.allWithDugnads || dugnadStats.allWithDugnads.length === 0) ? (
            <div className="text-center py-8 text-gray-400">
              <p>Ingen dugnadsdeltakelser registrert i {new Date().getFullYear()}</p>
              <p className="text-sm mt-1">Aktiviteter med "Dugnad" i navnet vil telles automatisk.</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2">
              {dugnadStats.allWithDugnads?.map((member, index) => {
                const isTopThree = index < 3;
                const hasGoldCrown = member.count >= 3;
                return (
                  <div 
                    key={member.userId}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      hasGoldCrown 
                        ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20' 
                        : 'bg-gray-700/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-500 text-gray-900' :
                        index === 1 ? 'bg-gray-300 text-gray-900' :
                        index === 2 ? 'bg-amber-600 text-white' :
                        'bg-gray-600 text-gray-300'
                      }`}>
                        {index + 1}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {hasGoldCrown ? (
                          <CrownTooltip 
                            text={`🏆 Gullkrone oppnådd!\n${member.count} av 3 dugnader i ${new Date().getFullYear()}.\nDette medlemmet har fullført dugnadskravet for året.`}
                            variant="gold"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="drop-shadow-lg">
                              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#EAB308"/>
                              <path d="M19 19H5v-2h14v2z" fill="#EAB308"/>
                            </svg>
                          </CrownTooltip>
                        ) : (
                          <CrownTooltip 
                            text={`⏳ På vei mot gullkrone!\n${member.count} av 3 dugnader i ${new Date().getFullYear()}.\nMangler ${3 - member.count} dugnad${3 - member.count > 1 ? 'er' : ''} for å oppnå gullkrone.`}
                            variant="gray"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="opacity-60">
                              <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5z" fill="#9CA3AF"/>
                              <path d="M19 19H5v-2h14v2z" fill="#9CA3AF"/>
                            </svg>
                          </CrownTooltip>
                        )}
                        <span className={`font-medium ${hasGoldCrown ? 'text-white' : 'text-gray-300'}`}>
                          {member.userName}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0 text-right">
                      <div className={`text-2xl font-bold ${
                        hasGoldCrown 
                          ? 'bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent' 
                          : 'text-blue-400'
                      }`}>
                        {member.count}
                      </div>
                      <div className="text-xs text-gray-400">dugnader</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default StatisticsSection;

// ---------- Presentasjonsvisning (Digital Signage) Promo Panel ----------
const SIGNAGE_SLIDE_KEYS = [
  { key: 'show_klubbstatus', label: 'Klubbstatus (forsiden)' },
  { key: 'show_activity', label: 'Aktivitetsstatistikk (siste 30 dager)' },
  { key: 'show_top_members', label: 'Mest aktive medlemmer' },
  { key: 'show_trend', label: 'Aktivitetsutvikling (trend + banefordeling)' },
  { key: 'show_upcoming', label: 'Kommende aktiviteter (fra kalenderen)' },
  { key: 'show_milestones', label: 'Milepæler og klubbrekorder' },
  { key: 'show_leaders_1_5', label: 'Topp 10 skyteledere (1–5)' },
  { key: 'show_leaders_6_10', label: 'Topp 10 skyteledere (6–10)' },
  { key: 'show_dugnad', label: 'Dugnadsdeltakelse' },
  { key: 'show_merkeskyting', label: 'Merkeskyting (KPI-er, nivåer, topp 5, nyeste merker)' },
  { key: 'show_iframe', label: 'Ekstern nettside (iframe)' },
];
const UPCOMING_VIEW_OPTIONS = [
  { value: 'day', label: 'Dag (i dag)' },
  { value: 'week', label: 'Uke (neste 7 dager)' },
  { value: 'month', label: 'Måned (neste 31 dager)' },
  { value: 'list', label: 'Liste (neste 3 hendelser)' },
];
const STATS_PERIOD_OPTIONS = [
  { value: 'week', label: 'Siste uke (7 dager)' },
  { value: 'month', label: 'Siste måned (30 dager)' },
  { value: 'quarter', label: 'Siste kvartal (90 dager)' },
  { value: 'halfyear', label: 'Siste halvår (180 dager)' },
  { value: 'year', label: 'Siste år (12 måneder)' },
  { value: 'all', label: 'Alle tider' },
];

const SignagePromo = () => {
  const [info, setInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [pdfLoading, setPdfLoading] = React.useState(false);
  const [settings, setSettings] = React.useState(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [savingSettings, setSavingSettings] = React.useState(false);
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadSettings = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/api/admin/signage/settings`, authHeader());
      setSettings(r.data);
    } catch (e) { /* silent */ }
  }, [API_BASE]);

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      await axios.put(`${API_BASE}/api/admin/signage/settings`, settings, authHeader());
    } catch (e) { alert('Kunne ikke lagre innstillinger'); }
    setSavingSettings(false);
  };

  const loadInfo = React.useCallback(async () => {
    try {
      const t = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/admin/signage/info`, { headers: { Authorization: `Bearer ${t}` } });
      setInfo(r.data);
      return r.data;
    } catch (e) {
      console.error('[Signage] loadInfo failed:', e?.response?.status, e?.response?.data, e?.message);
      return null;
    }
  }, [API_BASE]);

  React.useEffect(() => { loadInfo(); loadSettings(); }, [loadInfo, loadSettings]);

  const publicUrl = info && info.token
    ? `${window.location.origin}/public/signage/${info.org_id}/${info.token}`
    : null;

  const generate = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE}/api/admin/signage/token`, {}, { headers: { Authorization: `Bearer ${t}` } });
      const next = { ...(info || {}), token: r.data.token, org_id: r.data.org_id };
      setInfo(next);
      setLoading(false);
      return next;
    } catch (e) {
      console.error('[Signage] generate failed:', e?.response?.status, e?.response?.data, e?.message);
      setLoading(false);
      return null;
    }
  };

  const openSignage = async () => {
    // Åpne et vindu SYNKRONT i click-handler for å unngå popup-blokk,
    // og sett location når URL er klar.
    const win = window.open('about:blank', '_blank');
    let target = info;
    if (!target || !target.token) {
      target = await generate();
    }
    if (!target || !target.token) {
      if (win) win.close();
      alert('Kunne ikke opprette lenke – prøv igjen.');
      return;
    }
    const url = `${window.location.origin}/public/signage/${target.org_id}/${target.token}`;
    if (win) {
      try { win.location.href = url; } catch (e) { window.location.href = url; }
    } else {
      // Popup blokkert → naviger samme fane
      window.location.href = url;
    }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    let target = info;
    if (!target || !target.token) {
      target = await generate();
    }
    if (!target || !target.token) { setPdfLoading(false); return; }
    try {
      const url = `${API_BASE}/api/public/signage/${target.org_id}/${target.token}/pdf`;
      const resp = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `presentasjonsvisning-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('[Signage] pdf download failed:', e?.response?.status, e?.message);
      alert('Kunne ikke laste ned PDF: ' + (e?.response?.status || e.message));
    }
    setPdfLoading(false);
  };

  return (
    <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-slate-900 rounded-xl p-6 border border-purple-500/30 shadow-xl">
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
        <div className="flex-1">
          <div className="text-purple-300 uppercase text-xs font-bold tracking-widest">Nyhet</div>
          <h2 className="text-2xl font-black text-white mt-1">Presentasjonsvisning – vis statistikken på storskjerm</h2>
          <p className="text-purple-100 text-sm mt-2 max-w-2xl">
            Automatisk slideshow med 7 slides som roterer i loop. Klikk skjermen for pause, bruk piltast for å navigere.
            Offentlig lenke beskyttet av hemmelig token. Ingen innlogging kreves på skjermen i klubbhuset.
          </p>
          {publicUrl && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-purple-200/70 uppercase tracking-widest">Lenke:</span>
              <code className="bg-black/40 text-purple-200 rounded px-2 py-1 truncate max-w-sm">{publicUrl}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(publicUrl); }}
                className="text-purple-200 hover:text-white underline"
                data-testid="copy-signage-url"
              >Kopier</button>
              <button
                onClick={generate}
                disabled={loading}
                className="text-red-300 hover:text-red-200 underline disabled:opacity-40"
                data-testid="regen-signage-token"
              >Reset lenke</button>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={openSignage}
            disabled={loading}
            className="px-5 py-3 bg-purple-500 hover:bg-purple-400 text-white rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg disabled:opacity-50"
            data-testid="open-signage-btn"
          >
            🖥️ Åpne presentasjonsvisning
          </button>
          <button
            onClick={downloadPdf}
            disabled={pdfLoading}
            className="px-5 py-3 bg-white/10 hover:bg-white/20 border border-purple-300/40 text-white rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg disabled:opacity-50"
            data-testid="download-signage-pdf-btn"
          >
            {pdfLoading ? '⏳ Genererer…' : '📄 Last ned PDF'}
          </button>
          <button
            onClick={() => setSettingsOpen(v => !v)}
            className="px-5 py-3 bg-white/10 hover:bg-white/20 border border-purple-300/40 text-white rounded-lg font-bold text-sm flex items-center gap-2 shadow-lg"
            data-testid="toggle-signage-settings-btn"
          >
            ⚙️ Innstillinger
          </button>
        </div>
      </div>
      {settingsOpen && settings && (
        <div className="mt-6 pt-6 border-t border-purple-500/30 space-y-4" data-testid="signage-settings-panel">
          <div>
            <h3 className="text-white font-bold text-base mb-3">Slides som skal vises</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SIGNAGE_SLIDE_KEYS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 text-sm text-purple-100 bg-white/5 rounded-lg px-3 py-2 hover:bg-white/10 cursor-pointer">
                  <input type="checkbox" checked={!!settings[key]} data-testid={`signage-toggle-${key}`}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.checked })}
                    className="w-4 h-4 accent-purple-500" />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-purple-100 text-sm">
              <span className="block font-bold mb-1">QR-kode: URL for medlemsregistrering</span>
              <input type="url" placeholder="https://din-klubb.aktivlogg.no/register"
                value={settings.qr_registration_url || ''}
                onChange={(e) => setSettings({ ...settings, qr_registration_url: e.target.value })}
                data-testid="signage-qr-url-input"
                className="w-full bg-slate-900 border border-purple-500/30 rounded-lg px-3 py-2 text-white" />
              <div className="text-purple-300/60 text-xs mt-1">La stå tom for å skjule QR-koden.</div>
            </label>
            <label className="text-purple-100 text-sm">
              <span className="block font-bold mb-1">Varighet per slide (sekunder)</span>
              <input type="number" min="4" max="60" value={settings.slide_duration_seconds || 12}
                onChange={(e) => setSettings({ ...settings, slide_duration_seconds: Number(e.target.value) || 12 })}
                data-testid="signage-duration-input"
                className="w-32 bg-slate-900 border border-purple-500/30 rounded-lg px-3 py-2 text-white" />
            </label>
            <label className="text-purple-100 text-sm">
              <span className="block font-bold mb-1">Statistikkperiode (Aktivitetsstatistikk-slide)</span>
              <select value={settings.stats_period || 'month'}
                onChange={(e) => setSettings({ ...settings, stats_period: e.target.value })}
                data-testid="signage-stats-period-select"
                className="w-full bg-slate-900 border border-purple-500/30 rounded-lg px-3 py-2 text-white">
                {STATS_PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="text-purple-300/60 text-xs mt-1">Definerer hvilket tidsrom aktivitetsstatistikken sammenligner mot.</div>
            </label>
            <label className="text-purple-100 text-sm">
              <span className="block font-bold mb-1">Kommende aktiviteter – visning</span>
              <select value={settings.upcoming_view || 'list'}
                onChange={(e) => setSettings({ ...settings, upcoming_view: e.target.value })}
                data-testid="signage-upcoming-view-select"
                className="w-full bg-slate-900 border border-purple-500/30 rounded-lg px-3 py-2 text-white">
                {UPCOMING_VIEW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="text-purple-300/60 text-xs mt-1">Bestemmer om sliden viser hendelser for dagen, uken, måneden eller de 3 neste.</div>
            </label>
            <label className="text-purple-100 text-sm">
              <span className="block font-bold mb-1">Antall kommende aktiviteter (kun i Liste-visning)</span>
              <input type="number" min="1" max="20" value={settings.upcoming_count ?? 3}
                onChange={(e) => setSettings({ ...settings, upcoming_count: Math.max(1, Math.min(20, Number(e.target.value) || 3)) })}
                data-testid="signage-upcoming-count-input"
                className="w-32 bg-slate-900 border border-purple-500/30 rounded-lg px-3 py-2 text-white" />
              <div className="text-purple-300/60 text-xs mt-1">1–20 hendelser. Bruk pilene for å øke/minske.</div>
            </label>
            <div className="text-purple-100 text-sm md:col-span-2">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <span className="font-bold">Ekstern nettside / video – URL for iframe-slides</span>
                <div className="flex items-center gap-4">
                  <a
                    href="https://live.megalink.no/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="signage-helper-link-megalink"
                    className="inline-flex items-center gap-1 text-xs text-purple-300/70 hover:text-purple-100 hover:underline transition-colors"
                  >
                    Megalink LIVE
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://www.inrx.org/ixl/stevner"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="signage-helper-link-inrx"
                    className="inline-flex items-center gap-1 text-xs text-purple-300/70 hover:text-purple-100 hover:underline transition-colors"
                  >
                    InrX LIVE
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              {(() => {
                const urls = Array.isArray(settings.iframe_urls) && settings.iframe_urls.length
                  ? settings.iframe_urls
                  : ((settings.iframe_url || '').trim() ? [settings.iframe_url] : ['']);
                const updateUrls = (next) => setSettings({ ...settings, iframe_urls: next, iframe_url: next[0] || '' });
                return (
                  <div className="space-y-2">
                    {urls.map((u, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <input type="url" placeholder="YouTube, Vimeo eller en vanlig nettadresse"
                          value={u || ''}
                          onChange={(e) => { const arr = [...urls]; arr[idx] = e.target.value; updateUrls(arr); }}
                          data-testid={`signage-iframe-url-input-${idx}`}
                          className="flex-1 bg-slate-900 border border-purple-500/30 rounded-lg px-3 py-2 text-white" />
                        <button type="button"
                          onClick={() => { const arr = urls.filter((_, i) => i !== idx); updateUrls(arr.length ? arr : ['']); }}
                          className="p-2 rounded-lg bg-rose-500/20 hover:bg-rose-500/40 text-rose-200"
                          title="Slett URL"
                          data-testid={`signage-iframe-url-delete-${idx}`}>
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    <button type="button"
                      onClick={() => { updateUrls([...urls, '']); }}
                      className="mt-2 px-4 py-2 rounded-lg bg-emerald-500/25 hover:bg-emerald-500/40 text-emerald-100 font-bold text-sm flex items-center gap-2"
                      data-testid="signage-iframe-url-add">
                      <Plus className="w-4 h-4" /> Legg til ny URL-slide
                    </button>
                  </div>
                );
              })()}
              <div className="text-purple-300/60 text-xs mt-2">Hver URL blir én slide. Tomme felt hoppes over. YouTube- og Vimeo-lenker konverteres automatisk til embed med autoplay + mute. 10 % margin på hver side.</div>
            </div>
            <label className="flex items-center gap-3 text-sm text-purple-100 md:col-span-2">
              <input type="checkbox" checked={!!settings.night_mode_enabled} data-testid="signage-nightmode-toggle"
                onChange={(e) => setSettings({ ...settings, night_mode_enabled: e.target.checked })}
                className="w-4 h-4 accent-purple-500" />
              <span>Nattmodus (23:00–06:00): dim skjermen med klubblogo for å spare skjerm/strøm</span>
            </label>
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={saveSettings} disabled={savingSettings}
              data-testid="save-signage-settings-btn"
              className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-white rounded-lg font-bold disabled:opacity-50">
              {savingSettings ? 'Lagrer…' : 'Lagre innstillinger'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};